// src/pages/AdminLogin.jsx
import React, { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [theme, setTheme] = useState("dark");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Redirect if already logged in and role is admin
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check role in Firestore
        const q = query(
          collection(db, "adminUsers"),
          where("email", "==", user.email)
        );
        const snap = await getDocs(q);
        const userData = snap.docs[0]?.data();

        if (userData?.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          await signOut(auth);
        }
      }
    });
    return unsubscribe;
  }, [navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isRegister) {
        await createUserWithEmailAndPassword(auth, email, password);
        alert("Admin account created successfully!");
      } else {
        // Sign in
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

        const user = userCredential.user;

        // Check role in Firestore
        const q = query(
          collection(db, "adminUsers"),
          where("email", "==", user.email)
        );
        const snap = await getDocs(q);
        const userData = snap.docs[0]?.data();

        if (!userData || userData.role !== "admin") {
          setError("Access denied: only admins can log in.");
          await signOut(auth);
          setLoading(false);
          return;
        }

        navigate("/admin/dashboard");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  }

  const toggleTheme = () =>
    setTheme(theme === "dark" ? "light" : "dark");

  const containerClass =
    theme === "dark"
      ? "min-h-screen overflow-hidden bg-[#071321] text-white"
      : "min-h-screen overflow-hidden bg-slate-100 text-slate-900";

  const inputClass =
    "w-full rounded-xl border p-3.5 placeholder:text-slate-400 outline-none transition focus:ring-2 " +
    (theme === "dark"
      ? "border-slate-700 bg-slate-950/70 text-white focus:border-blue-500 focus:ring-blue-500/20"
      : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20");

  const buttonClass =
    "w-full rounded-xl p-3.5 font-bold shadow-lg transition-all duration-300 " +
    (loading
      ? theme === "dark"
        ? "cursor-not-allowed bg-blue-500/60 text-white shadow-blue-950/20"
        : "cursor-not-allowed bg-blue-400/70 text-white shadow-blue-200"
      : theme === "dark"
      ? "bg-blue-600 text-white shadow-blue-950/40 hover:bg-blue-500"
      : "bg-blue-600 text-white shadow-blue-200 hover:bg-blue-500");

  const secondaryBtn =
    "w-full text-center mt-3 font-semibold transition " +
    (theme === "dark"
      ? "text-blue-300 hover:text-blue-200"
      : "text-blue-700 hover:text-blue-600");

  const errorClass =
    theme === "dark"
      ? "mb-5 rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-sm text-rose-300"
      : "mb-5 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700";

  return (
    <div className={`${containerClass} relative flex items-center justify-center p-4 sm:p-8`}>
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-[28rem] w-[28rem] rounded-full bg-cyan-500/10 blur-3xl" />
      <div className={`relative grid w-full max-w-5xl overflow-hidden rounded-[28px] border shadow-2xl lg:min-h-[620px] lg:grid-cols-[1fr_0.9fr] ${
        theme === "dark" ? "border-slate-700/70 bg-slate-900/80 shadow-black/40" : "border-slate-200 bg-white shadow-slate-300/60"
      }`}>
        <section className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-blue-700 via-blue-800 to-[#071b2e] p-10 lg:flex">
          <div className="pointer-events-none absolute -right-24 top-20 h-80 w-80 rounded-full border border-white/10" />
          <div className="pointer-events-none absolute -right-10 top-36 h-52 w-52 rounded-full border border-white/10" />
          <div className="relative">
            <div className="mb-16 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-xl font-black text-white ring-1 ring-white/20">C</span>
              <span>
                <span className="block text-lg font-extrabold tracking-wide">CHIDIZ WI-FI</span>
                <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-200">Administrator portal</span>
              </span>
            </div>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-cyan-200">Secure operations</p>
            <h1 className="max-w-md text-4xl font-extrabold leading-tight xl:text-5xl">
              Your network.<br />Your control room.
            </h1>
            <p className="mt-5 max-w-sm text-sm leading-6 text-blue-100/80">
              Manage packages, ticket inventory, customer payments and team access from one workspace.
            </p>
          </div>
          <div className="relative grid grid-cols-2 gap-3">
            {[
              ["01", "Package control"],
              ["02", "Ticket inventory"],
              ["03", "Payment insights"],
              ["04", "Team access"],
            ].map(([number, label]) => (
              <div key={number} className="rounded-xl border border-white/10 bg-white/[0.07] p-3">
                <span className="text-[10px] font-bold tracking-widest text-cyan-200">{number}</span>
                <p className="mt-1 text-xs font-semibold text-white">{label}</p>
              </div>
            ))}
          </div>
          <p className="relative mt-8 text-xs text-blue-200/60">Protected access for authorized administrators.</p>
        </section>

        <section className="flex flex-col justify-center px-6 py-8 sm:px-10 lg:px-12">
          <div className="mb-9 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 lg:hidden">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-lg font-black text-white">C</span>
              <span className="text-sm font-extrabold tracking-wide">CHIDIZ WI-FI</span>
            </div>
            <span className="hidden text-xs font-bold uppercase tracking-[0.18em] text-blue-400 lg:block">Admin access</span>
            <button
              onClick={toggleTheme}
              type="button"
              className={`ml-auto rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                theme === "dark" ? "border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700" : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
          </div>

          <div className="mb-7">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/15 text-blue-400">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="4" y="10" width="16" height="11" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight">
              {isRegister ? "Create your account" : "Welcome back"}
            </h2>
            <p className={`mt-2 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
              {isRegister ? "Register an administrator account to continue." : "Sign in to open your management workspace."}
            </p>
          </div>

          {error && <div className={errorClass} role="alert">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="admin-email" className={`mb-2 block text-xs font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
                Email address
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="admin-password" className={`mb-2 block text-xs font-bold uppercase tracking-wider ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
                Password
              </label>
              <div className="relative">
                <input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={isRegister ? "new-password" : "current-password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-20`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className={`absolute inset-y-0 right-0 px-4 text-xs font-semibold ${theme === "dark" ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className={buttonClass}>
              {loading
                ? isRegister ? "Creating account…" : "Signing in…"
                : isRegister ? "Create admin account" : "Sign in to dashboard"}
              {!loading && <span className="ml-2" aria-hidden="true">→</span>}
            </button>
          </form>

          <div className={`mt-7 border-t pt-6 text-center ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
            <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
              {isRegister ? "Already have an account?" : "Need to set up an account?"}
            </p>
            <button
              onClick={() => {
                setError("");
                setIsRegister((current) => !current);
              }}
              className={secondaryBtn}
            >
              {isRegister ? "Return to sign in" : "Create admin account"}
            </button>
          </div>
          <p className={`mt-8 text-center text-[11px] ${theme === "dark" ? "text-slate-600" : "text-slate-400"}`}>
            © {new Date().getFullYear()} Chidiz Wi-Fi · Authorized access only
          </p>
        </section>
      </div>
    </div>
  );
}
